import { jsx as _jsx } from "react/jsx-runtime";
// Popover: shadcn/ui's Popover, restyled as the kit's menu surface (.wp-menu): solid panel, strong
// hairline, deep shadow, 6 px off its trigger. Opening moves focus to its first control; Tab wraps
// inside it; Escape, a click outside (a tap, on touch: not a scroll) or its trigger closes it and
// focus returns to the trigger (not after a click elsewhere, which keeps its own focus).
//
// The surface's own Root, Trigger and Content over Radix's Popper (positioning) and DismissableLayer
// (Escape and outside clicks), not Radix's Popover: that one writes focus guards into document.body
// whatever its container (src/ui/layer.ts says why that matters).
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { Popper, PopperAnchor, PopperContent } from "@radix-ui/react-popper";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { cn } from "./cn.js";
import { compose, composeRefs, focusedIn, LayerParts, useFocusScope, useLayerPart, useLayerParts, useOpenState, } from "./layer.js";
import { usePortalContainer } from "./portal.js";
const PopoverContext = createContext(null);
function usePopover(part) {
    const ctx = useContext(PopoverContext);
    if (ctx === null)
        throw new Error(`${part} must be inside a Popover`);
    return ctx;
}
export function Popover({ children, ...state }) {
    const [open, setOpen] = useOpenState(state);
    const contentId = useId();
    const trigger = useRef(null);
    const [hasAnchor, setHasAnchor] = useState(false);
    const value = useMemo(() => ({ open, setOpen, contentId, trigger, hasAnchor, setHasAnchor }), [open, setOpen, contentId, hasAnchor]);
    return (_jsx(Popper, { children: _jsx(PopoverContext, { value: value, children: children }) }));
}
export function PopoverTrigger({ asChild = false, ref, onClick, ...props }) {
    const ctx = usePopover("PopoverTrigger");
    const Comp = asChild ? Slot : "button";
    const button = (_jsx(Comp, { type: "button", "aria-haspopup": "dialog", "aria-expanded": ctx.open, "aria-controls": ctx.open ? ctx.contentId : undefined, "data-state": ctx.open ? "open" : "closed", ...props, ref: composeRefs(ref, ctx.trigger), onClick: compose(onClick, () => {
            ctx.setOpen(!ctx.open);
        }) }));
    return ctx.hasAnchor ? button : _jsx(PopperAnchor, { asChild: true, children: button });
}
/** Positions the content against this element instead of the trigger. */
export function PopoverAnchor(props) {
    const { setHasAnchor } = usePopover("PopoverAnchor");
    useEffect(() => {
        setHasAnchor(true);
        return () => {
            setHasAnchor(false);
        };
    }, [setHasAnchor]);
    return _jsx(PopperAnchor, { ...props });
}
export function PopoverClose({ asChild = false, onClick, ...props }) {
    const { setOpen } = usePopover("PopoverClose");
    const Comp = asChild ? Slot : "button";
    return (_jsx(Comp, { type: "button", ...props, onClick: compose(onClick, () => {
            setOpen(false);
        }) }));
}
const popoverVariants = cva([
    "z-modal rounded-12 border border-wp-line-strong bg-wp-bg-2 font-sans leading-body text-wp-text",
    "shadow-3 outline-none",
], {
    variants: {
        compact: { false: "w-62 p-1.5 text-13", true: "w-56 p-1 text-12" },
    },
    defaultVariants: { compact: false },
});
export function PopoverContent({ container, ...props }) {
    const ctx = usePopover("PopoverContent");
    const target = usePortalContainer(container);
    if (!ctx.open || target === null)
        return null;
    return createPortal(_jsx(PopoverLayer, { ctx: ctx, ...props }), target);
}
function PopoverLayer({ ctx, className, compact, align = "center", sideOffset = 6, collisionPadding = 8, onKeyDown, ref, ...props }) {
    const [content, setContent] = useState(null);
    const contentRef = useMemo(() => composeRefs(ref, setContent), [ref]);
    // Focus goes back to the trigger on close, unless the user moved on to something outside.
    const leftOutside = useRef(false);
    const tab = useFocusScope(content, {
        trapped: false,
        onUnmount: (opener) => {
            if (!leftOutside.current)
                (ctx.trigger.current ?? opener)?.focus({ preventScroll: true });
        },
    });
    // Presses outside it. Radix's DismissableLayer hears pointerdown on the document in the bubble
    // phase, so a page that stops its propagation (on an element, or in the capture phase on window)
    // would keep the popover open. This listens on window in the capture phase, before the page can
    // stop anything (short of stopImmediatePropagation on a window listener of its own added
    // earlier), and judges the press there: inside if its path holds the content or the trigger
    // (which toggles the popover itself). In a closed shadow root a window listener's path stops at
    // the host, so a press there is hit-tested in the root itself. A mouse press outside closes the
    // popover at once; a touch does when it ends as a tap (pointerup), not as a pan the browser
    // cancels (pointercancel).
    //
    // Nested portals. This test reads the DOM, where a portal puts a layer opened from inside the
    // popover (a menu in a menu, a tooltip, a dialog) somewhere else; React's tree, where it is
    // inside, is out of reach when the page stops the press before it reaches the surface's root.
    // So the surface's own layers register their elements with every popover they were opened from
    // (layer.ts, LayerParts), and a press on one of those parts is inside. Anything else portaled
    // from inside a popover (another library's portal) is outside: register it with useLayerPart.
    const [parts, register] = useLayerParts();
    useLayerPart(content);
    const { setOpen, trigger } = ctx;
    useEffect(() => {
        const win = content?.ownerDocument.defaultView;
        if (content == null || win == null)
            return;
        const root = content.getRootNode();
        const host = root instanceof ShadowRoot ? root.host : null;
        const ours = (node) => node instanceof Node &&
            (content.contains(node) ||
                trigger.current?.contains(node) === true ||
                [...parts].some((part) => part.contains(node)));
        const inside = (press) => {
            const path = press.composedPath();
            if (path.some(ours))
                return true;
            if (host === null || !path.includes(host) || !(root instanceof ShadowRoot))
                return false;
            return ours(root.elementFromPoint(press.clientX, press.clientY));
        };
        const close = () => {
            leftOutside.current = true;
            setOpen(false);
        };
        let endTouch = null;
        const onPointerDown = (press) => {
            if (inside(press))
                return;
            if (press.pointerType !== "touch") {
                close();
                return;
            }
            endTouch?.();
            const end = (ended) => {
                endTouch?.();
                if (ended.type === "pointerup")
                    close();
            };
            win.addEventListener("pointerup", end, true);
            win.addEventListener("pointercancel", end, true);
            endTouch = () => {
                win.removeEventListener("pointerup", end, true);
                win.removeEventListener("pointercancel", end, true);
                endTouch = null;
            };
        };
        win.addEventListener("pointerdown", onPointerDown, true);
        return () => {
            win.removeEventListener("pointerdown", onPointerDown, true);
            endTouch?.();
        };
    }, [content, setOpen, trigger, parts]);
    return (_jsx(LayerParts, { value: register, children: _jsx(DismissableLayer, { asChild: true, onPointerDownOutside: (event) => {
                event.preventDefault(); // presses outside are judged above
            }, onInteractOutside: (event) => {
                if (event.defaultPrevented)
                    return; // a press, judged above; what is left is focus
                const trigger = ctx.trigger.current;
                const onTrigger = trigger !== null && focusedIn(trigger) === trigger;
                if (onTrigger)
                    event.preventDefault();
                else
                    leftOutside.current = true;
            }, onDismiss: () => {
                ctx.setOpen(false);
            }, children: _jsx(PopperContent, { role: "dialog", id: ctx.contentId, "data-state": "open", tabIndex: -1, align: align, sideOffset: sideOffset, collisionPadding: collisionPadding, "data-compact": compact ?? false, className: cn(popoverVariants({ compact }), className), ...props, ref: contentRef, onKeyDown: compose(onKeyDown, tab) }) }) }));
}
//# sourceMappingURL=popover.js.map